import { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  Filter,
  TextInput,
  SelectInput,
  Show,
  useRecordContext,
  useRefresh,
  useNotify,
  Button,
  type RaRecord,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { Info, Link as LinkIcon, Payment, ReceiptLong, Storage } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { useEntitlement } from '@/components/domain-engine/useEntitlement';
import { DEFAULT_TENANT_ID } from '@/components/domain-engine/TenantSelector';
import { ShowHero, StatCard, SectionPaper, FieldPair, formatTimestamp } from './show-layout';
import { useBreadcrumbSafe } from '@/ui/Breadcrumb';

/**
 * Service Order admin resource — operator read surface for all tenant
 * pay-on-behalf orders (Milestone 08, sprint 02).
 *
 * Read-only (`list`/`show`): orders originate from BluePages, so no create or
 * edit affordances are registered. The one mutation is the status-gated
 * RefundAction, driving M07's existing `POST /service-orders/:id/refund`
 * lifecycle. No cancel button (sponsor-only, D5); no simulate-payment button
 * (dev-flag tooling stays CLI/curl).
 *
 * @see Documentation/Specification/020_Definitions/application-agnostic/ledger/ServiceOrder.md
 * @see sprints/2026-10/Milestone_08_ServiceCatalogueAdminUI/sprint-02-service-order-admin-surface/README.md
 */

type OrderStatus =
  | 'PendingPayment'
  | 'Paid'
  | 'Fulfilled'
  | 'Failed'
  | 'Refunded'
  | 'Cancelled'
  | 'Expired';

const STATUS_COLORS: Record<OrderStatus, 'info' | 'success' | 'primary' | 'error' | 'warning' | 'default'> = {
  PendingPayment: 'info',
  Paid: 'success',
  Fulfilled: 'primary',
  Failed: 'error',
  Refunded: 'warning',
  Cancelled: 'default',
  Expired: 'default',
};

const REFUNDABLE: OrderStatus[] = ['Paid', 'Failed', 'Expired'];

const STATUS_CHOICES = (Object.keys(STATUS_COLORS) as OrderStatus[]).map((id) => ({
  id,
  name: id,
}));

const REASON_CHOICES = [
  { id: 'Duplicate', name: 'Duplicate' },
  { id: 'ServiceIssue', name: 'Service Issue' },
  { id: 'ClientCredit', name: 'Client Credit' },
  { id: 'ContractCancellation', name: 'Contract Cancellation' },
  { id: 'Other', name: 'Other' },
];

function StatusChip({ value }: { value: string }) {
  const color = STATUS_COLORS[value as OrderStatus] ?? 'default';
  return <Chip label={value} color={color} size="small" />;
}

function AmountField({ record }: { record?: RaRecord }) {
  const amount = (record?.amount as { value?: number } | undefined)?.value;
  if (amount === undefined) return null;
  return <span>R {Number(amount).toFixed(2)}</span>;
}

function RefField({ source, label }: { source: string; label: string }) {
  const record = useRecordContext<RaRecord>();
  if (!record) return null;
  const ref = record[source] as { type?: string; id?: string } | undefined;
  if (!ref?.id) return <span>—</span>;
  return <span title={`${label}: ${ref.id}`}>{ref.id}</span>;
}

/**
 * Status-gated refund action (mirrors identity-verification `ReviewActions`
 * fetch style). Visible only for `Paid`/`Failed`/`Expired` orders when the
 * tenant holds the `ledger` domain entitlement.
 */
function RefundAction() {
  const record = useRecordContext<RaRecord>();
  const refresh = useRefresh();
  const notify = useNotify();
  const { hasEntitlement, isLoading } = useEntitlement(DEFAULT_TENANT_ID);
  const [open, setOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState('Duplicate');
  const [reasonNote, setReasonNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!record) return null;
  const status = String(record.status) as OrderStatus;
  if (!REFUNDABLE.includes(status) || isLoading || !hasEntitlement('ledger')) {
    return null;
  }

  const isOther = reasonCode === 'Other';
  const noteMissing = isOther && !reasonNote.trim();
  const apiBase =
    window.__ROSTA_API_URL__ || 'http://localhost:3200/api/v1';

  const handleSubmit = async () => {
    if (noteMissing) {
      notify('reasonNote is required when reasonCode is Other', {
        type: 'warning',
      });
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('rosta_token');
      const res = await fetch(`${apiBase}/service-orders/${record.id}/refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          reasonCode,
          ...(reasonNote.trim() ? { reasonNote: reasonNote.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}) as { detail?: string });
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      notify('Refund requested', { type: 'success' });
      setOpen(false);
      setReasonCode('Duplicate');
      setReasonNote('');
      refresh();
    } catch (err) {
      notify(`Refund failed: ${(err as Error).message}`, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button label="Refund" onClick={() => setOpen(true)} />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Refund order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Order {String(record.id)} — R{' '}
            {record.amount ? Number((record.amount as { value?: number }).value).toFixed(2) : '?'}
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="refund-reason-label">Reason code</InputLabel>
            <Select
              labelId="refund-reason-label"
              label="Reason code"
              value={reasonCode}
              onChange={(e) => setReasonCode(String(e.target.value))}
            >
              {REASON_CHOICES.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MuiTextField
            label={isOther ? 'Reason note (required)' : 'Reason note'}
            value={reasonNote}
            onChange={(e) => setReasonNote(e.target.value)}
            required={isOther}
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={() => setOpen(false)} />
          <Button
            label="Submit refund"
            onClick={handleSubmit}
            disabled={submitting || noteMissing}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function OrderFilter(props: object) {
  return (
    <Filter {...props}>
      <SelectInput source="status" label="Status" choices={STATUS_CHOICES} alwaysOn />
      <TextInput source="serviceKey" label="Service Key" alwaysOn />
    </Filter>
  );
}

export function ServiceOrderList() {
  return (
    <List filters={<OrderFilter />} title="Service Orders">
      {/* RA 5.15 reads bulkActionButtons from Datagrid, not List. No delete
          affordance for orders (D2): refund is the only terminal action. */}
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <FunctionField
          label="Status"
          render={(record: RaRecord) => <StatusChip value={String(record.status)} />}
        />
        <TextField source="serviceKey" label="Service Key" />
        <FunctionField
          label="Amount"
          render={(record: RaRecord) => <AmountField record={record} />}
        />
        <TextField source="payerRef.id" label="Payer" />
        <TextField source="sponsorVisibility" label="Visibility" />
        <TextField source="metadata.createdAt" label="Created" />
      </Datagrid>
    </List>
  );
}

/** Breadcrumb title: stable service key (orders have no display name). */
function OrderTitle() {
  const record = useRecordContext<RaRecord>();
  if (!record) return 'Service Order';
  return String(record.serviceKey) || 'Service Order';
}

function OrderContent() {
  const record = useRecordContext<RaRecord>();
  const navigate = useNavigate();
  const breadcrumb = useBreadcrumbSafe();
  const setSegments = breadcrumb?.setSegments;
  const clearSegments = breadcrumb?.clearSegments;

  // Replace the auto path crumb (raw URI-encoded document id) with a named
  // trail while this show page is mounted; falls back to path crumbs after
  // navigation (no provider / unmounted).
  useEffect(() => {
    if (!setSegments || !clearSegments) return;
    setSegments([
      { label: 'Home', path: '/' },
      { label: 'Service Orders', path: '/service-orders' },
      {
        label: String(record?.serviceKey || 'Details'),
        path: null,
      },
    ]);
    return () => clearSegments();
  }, [record?.id, record?.serviceKey, setSegments, clearSegments]);

  if (!record) return null;

  const status = String(record.status) as OrderStatus;
  const shortId = String(record.id).split(':').pop() ?? String(record.id);
  const payer = String((record.payerRef as { id?: string } | undefined)?.id ?? '—');
  const catalogueId = (record.catalogueRef as { id?: string } | undefined)?.id;

  const openCatalogue = () => {
    if (catalogueId) navigate(`/service-catalogues/${encodeURIComponent(catalogueId)}/show`);
  };

  return (
    <Box sx={{ p: 3, pb: 5 }}>
      <PageDescription
        entity="Service Order"
        description="Pay-on-behalf order for a sponsored service. Originates in BluePages; status tracks payment through fulfilment."
        icon={<ReceiptLong />}
      />

      <ShowHero
        icon={<Payment />}
        title={String(record.serviceKey)}
        subtitle={`Order #${shortId}  ·  payer ${payer}`}
        chips={
          <>
            <StatusChip value={status} />
            <Chip
              label={String(record.sponsorVisibility || 'Default')}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.18)',
                color: 'inherit',
                fontWeight: 600,
              }}
            />
          </>
        }
        actions={
          <>
            {catalogueId && (
              <Button
                label="View catalogue"
                variant="outlined"
                color="inherit"
                onClick={openCatalogue}
              />
            )}
            <RefundAction />
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Amount">
            <Box
              sx={{
                '& .MuiTypography-root': {
                  fontSize: '1.5rem',
                  fontWeight: 700,
                },
              }}
            >
              <FunctionField
                label="Amount"
                render={(r: RaRecord) => <AmountField record={r} />}
              />
            </Box>
          </StatCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Status">
            <FunctionField
              label="Status"
              render={(r: RaRecord) => <StatusChip value={String(r.status)} />}
            />
          </StatCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Expires">
            <FunctionField
              label="Expires At"
              render={(r: RaRecord) => (
                <Typography variant="body1" fontWeight={600}>
                  {formatTimestamp(String(r.expiresAt ?? ''))}
                </Typography>
              )}
            />
          </StatCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Created">
            <Typography variant="body1" fontWeight={600}>
              {formatTimestamp(
                String(
                  (record.metadata as { createdAt?: string } | undefined)?.createdAt ?? '',
                ),
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              by {String((record.metadata as { createdBy?: string } | undefined)?.createdBy ?? 'system')}
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <SectionPaper icon={<Info />} title="Order">
            <FieldPair label="Service Key">
              <TextField source="serviceKey" label="Service Key" />
            </FieldPair>
            <FieldPair label="Payer">
              <TextField source="payerRef.id" label="Payer" />
            </FieldPair>
            <FieldPair label="Sponsor Visibility">
              <TextField source="sponsorVisibility" label="Sponsor Visibility" />
            </FieldPair>
          </SectionPaper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <SectionPaper icon={<LinkIcon />} title="Payment & fulfilment">
            <FieldPair label="Payment Ref">
              <TextField source="paymentRef" label="Payment Ref" emptyText="—" />
            </FieldPair>
            <FieldPair label="Payment Provider">
              <TextField source="paymentProvider" label="Payment Provider" emptyText="—" />
            </FieldPair>
            <FieldPair label="Fulfilment Ref">
              <TextField source="fulfillmentRef" label="Fulfilment Ref" emptyText="—" />
            </FieldPair>
            <FieldPair label="Beneficiary">
              <RefField source="beneficiaryRef" label="Beneficiary" />
            </FieldPair>
            <FieldPair label="Service">
              <RefField source="serviceRef" label="Service" />
            </FieldPair>
            <FieldPair label="Catalogue">
              <RefField source="catalogueRef" label="Catalogue" />
            </FieldPair>
            <FieldPair label="Receipt">
              <RefField source="receiptRef" label="Receipt" />
            </FieldPair>
            <FieldPair label="Transaction">
              <RefField source="transactionRef" label="Transaction" />
            </FieldPair>
          </SectionPaper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <SectionPaper icon={<Storage />} title="Record & audit">
            <FieldPair label="URN">
              <TextField source="urn" label="URN" />
            </FieldPair>
            <FieldPair label="Document ID">
              <TextField source="_id" label="Document ID" />
            </FieldPair>
            <FieldPair label="Created By">
              <TextField source="metadata.createdBy" label="Created By" />
            </FieldPair>
            <FieldPair label="Updated">
              <FunctionField
                source="metadata.updatedAt"
                label="Updated"
                render={(r: RaRecord) =>
                  formatTimestamp(
                    String(
                      (r.metadata as { updatedAt?: string } | undefined)?.updatedAt ?? '',
                    ),
                  )
                }
              />
            </FieldPair>
            <FieldPair label="Updated By">
              <TextField source="metadata.updatedBy" label="Updated By" />
            </FieldPair>
          </SectionPaper>
        </Grid>
      </Grid>
    </Box>
  );
}

export function ServiceOrderShow() {
  return (
    <Show title={<OrderTitle />}>
      <OrderContent />
    </Show>
  );
}
